import React from "react"
import {
  type EntityCategory,
  type EntityParty,
  LedgerType,
  type PushAction,
  type WorkItem,
  type WorkItemPushParams,
  WorkItemReviewState,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgIconStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgArrowCircleRightSolid } from "@digits-shared/components/SVGIcons/solid/ArrowCircleRightSolid.svg"
import { SvgEdit02Solid } from "@digits-shared/components/SVGIcons/solid/Edit02Solid.svg"
import colors from "@digits-shared/themes/colors"
import styled, { css } from "styled-components"
import { TRANSACTION_REVIEW_TYPES } from "src/frontend/components/OS/Applications/Boost/WorkItems/hooks/useListTransactionReviewItemsQuery"
import { DialogOption } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Actions/ActionDialog"
import { getSupportingTransactions } from "src/frontend/components/OS/Shared/WorkItems/useSupportingTransactions"
import { isNewEntityId } from "src/shared/components/Typeahead/sharedTypeahead"

export const ESTIMATED_VENDOR_DIALOG_HEIGHT = 200

export const ActionHoverContainer = styled.div<{
  disabled?: boolean
}>`
  position: relative;
  ${({ disabled }) =>
    disabled &&
    css`
      ${DialogOption} {
        cursor: auto;
      }
    `}
`

export const VendorDialogOptionText = styled.div`
  flex-direction: column;
  align-items: flex-start;
  line-height: 16px;
  flex: 1;
  margin-left: 4px;
`

const DIALOG_ICON_STYLES = css`
  width: 16px;
  height: 16px;
  ${svgIconStyles(colors.secondary)};
`

export const UpdateVendorIcon = styled(SvgArrowCircleRightSolid)`
  ${DIALOG_ICON_STYLES};
`

export const RenameVendorIcon = styled(SvgEdit02Solid)`
  ${DIALOG_ICON_STYLES};
`

// The party that will be pushed to QuickBooks.
export function useUpdateParty(workItem: WorkItem): EntityParty | undefined | null {
  // need to add transactions to the dependency list because it gets mutated in the cache
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => getUpdateParty(workItem), [workItem, workItem.transactions])
}

export function getUpdateParty(workItem: WorkItem): EntityParty | undefined | null {
  if (!workItem) return undefined

  const { to: defaultTo, userProvided } = getSupportingTransactions(workItem)

  // Ignore `to` once the client has responded
  const to = workItem.reviewState === WorkItemReviewState.ClientResponded ? undefined : defaultTo

  return userProvided?.party || to?.party
}

// The category that will be or was pushed to QuickBooks.
export function useUpdateCategory(workItem: WorkItem): EntityCategory | undefined | null {
  // need to add transactions to the dependency list because it gets mutated in the cache
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => getUpdateCategory(workItem), [workItem, workItem.transactions])
}

export function getUpdateCategory(workItem: WorkItem): EntityCategory | undefined | null {
  if (!workItem) return undefined

  const { to: defaultTo, userProvided, responseBased } = getSupportingTransactions(workItem)

  // Ignore `to` once the client has responded
  const to = workItem.reviewState === WorkItemReviewState.ClientResponded ? undefined : defaultTo

  return userProvided?.displayCategory || to?.displayCategory || responseBased?.displayCategory
}

export function buildPartyParamWorkItem(
  w: WorkItem,
  action: PushAction,
  skipClose?: boolean
): WorkItemPushParams | undefined {
  const updateParty = getUpdateParty(w)
  if (!updateParty) {
    return
  }

  return {
    workItemId: w.workItemId,
    partyParams: isNewEntityId(updateParty.id)
      ? {
          partyName: updateParty.name,
          action,
        }
      : {
          partyID: updateParty.id,
          partyName: updateParty.name,
          partyIconUrl: updateParty.iconUrl,
          action,
        },
    skipClose,
  }
}

export function buildCategoryParamWorkItem(
  w: WorkItem,
  skipClose?: boolean
): WorkItemPushParams | undefined {
  const updateCategory = getUpdateCategory(w)
  if (!updateCategory) {
    return
  }

  return {
    workItemId: w.workItemId,
    appendCommentsToQBO:
      TRANSACTION_REVIEW_TYPES.includes(w.itemType) && w.ledgerType === LedgerType.QuickBooks,
    categoryParams: {
      categoryIcon: updateCategory.displayKey,
      categoryId: updateCategory.id,
      categoryName: updateCategory.name,
    },
    skipClose,
  }
}
